import { api } from './api.ts';

const BASE = '/api/v1/fii';

// ── Types ──────────────────────────────────────────────────────────────────

export interface FIIAsset {
  id: number;
  user_id: number;
  ticker: string;
  segment: string;
  objective: string;
  target_allocation_pct: number;
  ceiling_price: number | null;
  use_pvp: boolean;
  cnpj: string | null;
  fund_name: string;
  created_at: string;
  updated_at: string;
}

export interface FIIAssetPortfolioItem extends FIIAsset {
  quantity: number;
  average_price: number;
  total_cost: number;
  current_quote: number;
  current_value: number;
  profitability_rs: number | null;
  profitability_pct: number | null;
  total_income: number | null;
  total_return_rs: number | null;
  total_return_pct: number | null;
  ceiling_price: number | null;
  discount_pct: number | null;
  current_allocation_pct: number | null;
  action: 'Aportar' | 'Resgatar' | 'Equilíbrio' | null;
  rebalance_amount: number | null;
  quote_stale: boolean;
  allocation_warning?: string;
  use_pvp: boolean;
}

export interface FIIPortfolioResponse {
  total_invested: number;
  total_current_value: number;
  total_profitability_rs: number;
  total_profitability_pct: number | null;
  total_income: number;
  total_return_pct: number | null;
  quotes_stale: boolean;
  quotes_updated_at: string;
  assets: FIIAssetPortfolioItem[];
}

export interface CreateFIIAssetRequest {
  ticker: string;
  segment: string;
  objective?: string;
  target_allocation_pct: number;
  ceiling_price?: number | null;
  use_pvp?: boolean;
  cnpj?: string;
  fund_name?: string;
}

export interface UpdateFIIAssetRequest {
  segment?: string;
  objective?: string;
  target_allocation_pct?: number;
  ceiling_price?: number | null;
  use_pvp?: boolean;
  cnpj?: string;
  fund_name?: string;
}

export interface CNPJLookupResponse {
  cnpj: string;
  fund_name: string;
}

export interface PVPResponse {
  ticker: string;
  book_value: number;
  price_to_book: number;
  fetched_at: string;
}

export interface FIIOperation {
  id: number;
  user_id: number;
  fii_asset_id: number;
  fii_asset_ticker?: string;
  date: string;
  type: 'buy' | 'sell';
  quantity: number;
  unit_price: number;
  total_value: number;
  irrf: number;
  created_at: string;
}

export interface CreateFIIOperationRequest {
  fii_asset_id: number;
  date: string;
  type: 'buy' | 'sell';
  quantity: number;
  unit_price: number;
  irrf?: number;
}

export interface FIIIncome {
  id: number;
  user_id: number;
  fii_asset_id: number;
  fii_asset_ticker?: string;
  date: string;
  type: 'dividendo' | 'jcp';
  value_per_share: number;
  total_value: number;
  warning?: string;
}

export interface CreateFIIIncomeRequest {
  fii_asset_id: number;
  date: string;
  type: 'dividendo' | 'jcp';
  total_value: number;
}

export interface FIIIncomeAggregatedResponse {
  aggregation: 'month' | 'year';
  data: Array<{
    period: string;
    total_value: number;
    by_asset: Array<{ ticker: string; total_value: number }>;
  }>;
  grand_total: number;
}

export interface FIIPricingItem {
  fii_asset_id: number;
  ticker: string;
  risk_premium_pct: number;
  average_monthly_income: number | null;
  selic_rate: number;
  ceiling_price: number | null;
  current_quote: number;
  discount_pct: number | null;
}

export interface DarfSellDetail {
  operation_id: number;
  ticker: string;
  date: string;
  quantity: number;
  sale_price: number;
  average_price: number;
  cost_basis: number;
  sale_value: number;
  gain_loss: number;
  irrf: number;
}

export interface DarfMonthEntry {
  reference_month: string;
  sells: DarfSellDetail[];
  total_sales: number;
  total_cost_basis: number;
  gross_result: number;
  accumulated_loss_before: number;
  taxable_profit: number;
  accumulated_loss_after: number;
  tax_rate: number;
  tax_amount_before_irrf: number;
  irrf_retido_mes: number;
  irrf_acumulado_before: number;
  irrf_compensado: number;
  irrf_acumulado_after: number;
  tax_amount: number;
  darf_amount: number;
  should_accumulate: boolean;
  due_date: string;
  is_paid: boolean;
  paid_at: string | null;
  is_overdue: boolean;
}

export interface DarfYearSummary {
  year: number;
  total_sales: number;
  total_gains: number;
  total_losses: number;
  total_tax_due: number;
  total_tax_paid: number;
  total_irrf_retido: number;
  total_irrf_compensado: number;
  irrf_nao_compensado: number;
}

export interface DarfControlResponse {
  months: DarfMonthEntry[];
  by_year: DarfYearSummary[];
  total_accumulated_loss: number;
  total_tax_due: number;
  total_tax_paid: number;
  total_tax_pending: number;
  total_irrf_retido: number;
  total_irrf_compensado: number;
  irrf_acumulado_atual: number;
}

export interface FIIDashboardResponse {
  portfolio: {
    total_invested: number;
    current_value: number;
    profitability_rs: number;
    profitability_pct: number;
    total_income: number;
    income_yield_pct: number;
    total_return_rs: number;
    total_return_pct: number;
    asset_count: number;
    quotes_stale: boolean;
    quotes_updated_at: string;
  };
  indices: {
    cdi: IndexEntry | null;
    ipca: IndexEntry | null;
    selic: IndexEntry | null;
    ifix: IndexEntry | null;
  };
}

export interface IndexEntry {
  value: number;
  period: string;
  fetched_at: string;
  stale: boolean;
}

export interface QuoteRefreshResponse {
  refreshed: number;
  failed: number;
  quotes: Array<{ ticker: string; price: number; fetched_at: string }>;
}

// ── API functions ──────────────────────────────────────────────────────────

// Assets
export const getAssets = () =>
  api.get<FIIAsset[]>(`${BASE}/assets`).then(r => r.data);

export const createAsset = (data: CreateFIIAssetRequest) =>
  api.post<{ asset: FIIAsset; warning?: string }>(`${BASE}/assets`, data).then(r => r.data);

export const updateAsset = (id: number, data: UpdateFIIAssetRequest) =>
  api.put<{ asset: FIIAsset; warning?: string }>(`${BASE}/assets/${id}`, data).then(r => r.data);

export const deleteAsset = (id: number) =>
  api.delete(`${BASE}/assets/${id}`).then(r => r.data);

// PVP (VPA lookup)
export const getAssetPVP = (ticker: string) =>
  api.get<PVPResponse>(`${BASE}/pvp/${ticker}`).then(r => r.data);

// CNPJ lookup (BrasilAPI / Receita Federal)
export const lookupCNPJ = (cnpj: string) =>
  api.get<CNPJLookupResponse>(`${BASE}/cnpj-lookup/${cnpj}`).then(r => r.data);

// Portfolio
export const getPortfolio = () =>
  api.get<FIIPortfolioResponse>(`${BASE}/portfolio`).then(r => r.data);

export const refreshQuotes = () =>
  api.get<QuoteRefreshResponse>(`${BASE}/quotes/refresh`).then(r => r.data);

export const getIndices = () =>
  api.get<FIIDashboardResponse['indices']>(`${BASE}/indices`).then(r => r.data);

// Operations
export const getOperations = (params?: {
  fii_asset_id?: number;
  type?: string;
  start_date?: string;
  end_date?: string;
}) => api.get<FIIOperation[]>(`${BASE}/operations`, { params }).then(r => r.data);

export const createOperation = (data: CreateFIIOperationRequest) =>
  api.post<FIIOperation>(`${BASE}/operations`, data).then(r => r.data);

export const updateOperation = (id: number, data: Partial<CreateFIIOperationRequest>) =>
  api.put<FIIOperation>(`${BASE}/operations/${id}`, data).then(r => r.data);

export const deleteOperation = (id: number) =>
  api.delete(`${BASE}/operations/${id}`).then(r => r.data);

// Income
export const getIncome = (params?: {
  fii_asset_id?: number;
  type?: 'dividendo' | 'jcp';
  start_date?: string;
  end_date?: string;
  group_by?: 'month' | 'year';
}) => api.get<FIIIncome[] | FIIIncomeAggregatedResponse>(`${BASE}/income`, { params }).then(r => r.data);

export const createIncome = (data: CreateFIIIncomeRequest) =>
  api.post<FIIIncome>(`${BASE}/income`, data).then(r => r.data);

export const updateIncome = (id: number, data: Partial<CreateFIIIncomeRequest>) =>
  api.put<FIIIncome>(`${BASE}/income/${id}`, data).then(r => r.data);

export const deleteIncome = (id: number) =>
  api.delete(`${BASE}/income/${id}`).then(r => r.data);

// Pricing
export const getPricing = () =>
  api.get<FIIPricingItem[]>(`${BASE}/pricing`).then(r => r.data);

export const updatePricing = (assetId: number, riskPremiumPct: number) =>
  api.put<FIIPricingItem>(`${BASE}/pricing/${assetId}`, { risk_premium_pct: riskPremiumPct }).then(r => r.data);

// DARF Control
export const getDarfControl = () =>
  api.get<DarfControlResponse>(`${BASE}/darf/control`).then(r => r.data);

export const markDarfPaid = (month: string) =>
  api.put(`${BASE}/darf/control/${month}/pay`).then(r => r.data);

export const markDarfUnpaid = (month: string) =>
  api.put(`${BASE}/darf/control/${month}/unpay`).then(r => r.data);

// Dashboard
export const getDashboard = () =>
  api.get<FIIDashboardResponse>(`${BASE}/dashboard`).then(r => r.data);

// ── IRPF Bens e Direitos ────────────────────────────────────────────────────

export interface AssetDeclarationItem {
  ticker: string;
  cnpj: string | null;
  fund_name: string;
  previous_year_quantity: number;
  previous_year_average_price: number;
  previous_year_total: number;
  current_year_quantity: number;
  current_year_average_price: number;
  current_year_total: number;
  discriminacao: string;
}

export interface AssetDeclarationReport {
  year: number;
  items: AssetDeclarationItem[];
  total_previous_year: number;
  total_current_year: number;
}

export const getAssetDeclaration = (year: number): Promise<AssetDeclarationReport> =>
  api.get<AssetDeclarationReport>(`${BASE}/asset-declaration`, { params: { year } }).then(r => r.data);
